import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchLeads, fetchLeadStats } from "@/lib/api";
import { Loader2, Lock, LogOut, Mail, Building2, Globe2, MessageSquare, RefreshCw } from "lucide-react";
import PageHero from "@/components/common/PageHero";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("kv_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(t), fetchLeadStats(t)]);
      setLeads(l);
      setStats(s);
      setAuthed(true);
    } catch (e) {
      toast.error("Invalid admin token");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const onLogin = (e) => {
    e.preventDefault();
    if (!input) return;
    localStorage.setItem("kv_admin_token", input);
    setToken(input);
  };

  const logout = () => {
    localStorage.removeItem("kv_admin_token");
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  if (!authed) {
    return (
      <main data-testid="admin-login-page">
        <PageHero eyebrow="Admin" title="Lead Dashboard" subtitle="Restricted access. Enter your admin token to continue." />
        <section className="pb-24">
          <form onSubmit={onLogin} className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl glass-heavy p-8">
              <div className="h-12 w-12 grid place-items-center rounded-2xl bg-blue-500/15 border border-blue-400/20 mb-6">
                <Lock className="h-5 w-5 text-blue-300" />
              </div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Admin Token</label>
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="admin-token-input"
                placeholder="Enter admin token"
                className="w-full bg-black/40 border border-white/10 focus:border-blue-500/40 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 transition text-white rounded-full px-6 py-3 font-semibold glow-blue"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
              </button>
              <p className="mt-4 text-xs text-zinc-500">Default token: <code className="text-zinc-400">kavion-admin-2026</code></p>
            </div>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="admin-page">
      <PageHero eyebrow="Admin Console" title="Leads Dashboard" subtitle="Captured leads from website forms.">
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => load(token)} className="inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 text-sm hover:bg-white/10 transition" data-testid="admin-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={logout} className="inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 text-sm hover:bg-white/10 transition" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </PageHero>

      {stats && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { l: "Total Leads", v: stats.total },
              { l: "Strategy Calls", v: stats.by_type?.strategy_call ?? 0 },
              { l: "Free Audits", v: stats.by_type?.audit ?? 0 },
              { l: "General", v: stats.by_type?.contact ?? 0 },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl glass p-6">
                <div className="text-xs uppercase tracking-wider text-zinc-500">{s.l}</div>
                <div className="font-display text-3xl font-extrabold text-white mt-2">{s.v}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {leads.length === 0 ? (
            <div className="rounded-3xl glass p-10 text-center text-zinc-400">No leads yet. Submit the contact form to see entries here.</div>
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="rounded-2xl glass p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`lead-row-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-lg font-bold text-white">{l.name}</span>
                      <span className="text-xs uppercase tracking-wider text-blue-300 bg-blue-500/10 border border-blue-400/20 rounded-full px-2.5 py-0.5">
                        {l.lead_type.replace("_", " ")}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-400">
                      <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-blue-400" /> {l.email}</span>
                      {l.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-blue-400" /> {l.company}</span>}
                      {l.website && <span className="inline-flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5 text-blue-400" /> {l.website}</span>}
                      {l.monthly_revenue && <span className="text-emerald-300">{l.monthly_revenue}</span>}
                    </div>
                    {l.message && (
                      <div className="mt-3 text-sm text-zinc-400 inline-flex items-start gap-2">
                        <MessageSquare className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                        <span>{l.message}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 shrink-0">
                    {new Date(l.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
